import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Paperclip } from 'lucide-react';
import { useState } from 'react';
import { EmptyState, PageHeader } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useOrgId } from '@/lib/org';
import { pdfAction } from '@/lib/pdf';
import { KIND_INFO, emailStatusLabel, kindLabel, type SentEmail } from '@/lib/sales';
import { cn } from '@/lib/utils';
import { useT } from '@/lib/i18n';

export const Route = createFileRoute('/settings/sent-emails')({ component: SentEmails });

interface Emails {
  transport: string;
  delivers: boolean;
  emails: SentEmail[];
}

/** Every email sent (or recorded) from a sales document, with a preview and the PDF. */
function SentEmails() {
  const t = useT();
  const orgId = useOrgId();
  const [openId, setOpenId] = useState<string | null>(null);
  const emails = useQuery({
    queryKey: ['sales', orgId, 'emails'],
    queryFn: () => api<Emails>(`/orgs/${orgId}/sales/emails`),
    enabled: Boolean(orgId),
  });
  const open = emails.data?.emails.find((e) => e.id === openId) ?? null;
  return (
    <>
      <PageHeader
        title={t('settingsPages.general.sentEmails.title')}
        description={
          emails.data && !emails.data.delivers
            ? t('settingsPages.general.sentEmails.descriptionKept')
            : t('settingsPages.general.sentEmails.description')
        }
      />
      {!orgId ? (
        <EmptyState title={t('shell.selectClient')} hint={t('home.selectClientHint')} />
      ) : emails.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : emails.isError ? (
        <EmptyState
          title={t('settingsPages.general.sentEmails.loadError')}
          hint={emails.error.message}
        />
      ) : !emails.data.emails.length ? (
        <EmptyState
          title={t('settingsPages.general.sentEmails.emptyTitle')}
          hint={t('settingsPages.general.sentEmails.emptyHint')}
        />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[150px]">
                  {t('settingsPages.general.sentEmails.colDateTime')}
                </TableHead>
                <TableHead>{t('settingsPages.general.sentEmails.to')}</TableHead>
                <TableHead>{t('settingsPages.general.sentEmails.colSubject')}</TableHead>
                <TableHead className="w-[150px]">
                  {t('settingsPages.general.sentEmails.document')}
                </TableHead>
                <TableHead className="w-[110px]">
                  {t('settingsPages.general.sentEmails.status')}
                </TableHead>
                <TableHead className="w-[200px]">
                  {t('settingsPages.general.sentEmails.colAttachment')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {emails.data.emails.map((e) => (
                <TableRow
                  key={e.id}
                  className="cursor-pointer"
                  data-testid="email-row"
                  onClick={() => setOpenId(e.id)}
                >
                  <TableCell className="tnum">{formatDateTime(e.createdAt)}</TableCell>
                  <TableCell>
                    {e.to}
                    {e.cc ? (
                      <div className="text-xs text-muted-foreground">
                        {t('settingsPages.general.sentEmails.cc')} {e.cc}
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell>{e.subject}</TableCell>
                  <TableCell onClick={(ev) => ev.stopPropagation()}>
                    <DocumentLink e={e} />
                  </TableCell>
                  <TableCell>
                    <EmailStatus e={e} />
                  </TableCell>
                  <TableCell onClick={(ev) => ev.stopPropagation()}>
                    {e.attachmentName ? <Attachment orgId={orgId} e={e} /> : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <Dialog open={Boolean(open)} onOpenChange={(o) => !o && setOpenId(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="email-preview">
          {open ? (
            <>
              <DialogHeader>
                <DialogTitle>{open.subject}</DialogTitle>
                <DialogDescription>
                  {formatDateTime(open.createdAt)}
                  {open.sentBy
                    ? ` · ${t('settingsPages.general.sentEmails.sentBy', { name: open.sentBy })}`
                    : ''}
                </DialogDescription>
              </DialogHeader>
              <dl className="grid grid-cols-[80px_1fr] gap-x-4 gap-y-1 text-sm">
                <dt className="text-muted-foreground">
                  {t('settingsPages.general.sentEmails.from')}
                </dt>
                <dd>{open.from}</dd>
                <dt className="text-muted-foreground">
                  {t('settingsPages.general.sentEmails.to')}
                </dt>
                <dd>{open.to}</dd>
                {open.cc ? (
                  <>
                    <dt className="text-muted-foreground">
                      {t('settingsPages.general.sentEmails.cc')}
                    </dt>
                    <dd>{open.cc}</dd>
                  </>
                ) : null}
                <dt className="text-muted-foreground">
                  {t('settingsPages.general.sentEmails.document')}
                </dt>
                <dd>
                  <DocumentLink e={open} />
                </dd>
                <dt className="text-muted-foreground">
                  {t('settingsPages.general.sentEmails.status')}
                </dt>
                <dd>
                  <EmailStatus e={open} />
                  {open.status === 'recorded' ? (
                    <span className="text-muted-foreground">
                      {' '}
                      {t('settingsPages.general.sentEmails.keptHere')}
                    </span>
                  ) : null}
                  {open.error ? <span className="text-destructive"> · {open.error}</span> : null}
                </dd>
              </dl>
              <div className="max-h-[320px] overflow-y-auto whitespace-pre-line rounded-md border bg-muted/30 p-3 text-sm">
                {open.body}
              </div>
              {open.attachmentName && orgId ? <Attachment orgId={orgId} e={open} /> : null}
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}

function DocumentLink({ e }: { e: SentEmail }) {
  if (!e.documentId || !e.documentKind) return null;
  const info = KIND_INFO[e.documentKind];
  return (
    <Link className="text-primary hover:underline" to={`${info.path}/${e.documentId}`}>
      {kindLabel(e.documentKind)} {e.documentNumber}
    </Link>
  );
}

function EmailStatus({ e }: { e: SentEmail }) {
  return (
    <span
      className={cn(
        'rounded-full border px-2 py-0.5 text-xs font-medium',
        e.status === 'failed' ? 'border-destructive text-destructive' : 'text-muted-foreground',
      )}
    >
      {emailStatusLabel(e.status)}
    </span>
  );
}

function Attachment({ orgId, e }: { orgId: string; e: SentEmail }) {
  const t = useT();
  const path = `/orgs/${orgId}/sales/emails/${e.id}/attachment`;
  return (
    <span className="inline-flex items-center gap-1 text-sm">
      <Paperclip className="size-4 text-muted-foreground" aria-hidden />
      <Button
        variant="link"
        size="sm"
        className="h-auto px-0"
        onClick={() => void pdfAction('preview', path, e.attachmentName!)}
      >
        {e.attachmentName}
      </Button>
      <Button
        variant="link"
        size="sm"
        className="h-auto px-1 text-muted-foreground"
        onClick={() => void pdfAction('download', `${path}?download=1`, e.attachmentName!)}
      >
        {t('settingsPages.general.sentEmails.download')}
      </Button>
    </span>
  );
}
